import { Modal, useToast } from '@croquiscom/pds';
import { GraphQLError } from 'graphql/index';
import { FC } from 'react';
import { Form, UpdateUserFormProvider, UpdateUserFormValues } from './Form';
import { useChangeSellerAccountInfoMutation } from '@/api';
import { usePasswordChangeRequired, useUser } from '@/common/hooks';

interface Props {
  opened?: boolean;
  close?: () => void;
  requirePasswordChange?: boolean;
}

export const UpdateUserModal: FC<Props> = ({ opened, close, requirePasswordChange = false }) => {
  const toast = useToast();
  const { user, refetch, logout } = useUser();
  const { setRequired } = usePasswordChangeRequired();
  const { mutate, isPending: is_loading } = useChangeSellerAccountInfoMutation({
    onSuccess: ({ change_seller_account_info }, { input }) => {
      if (change_seller_account_info) {
        toast.show({
          kind: 'success',
          position: 'bottom-center',
          content: requirePasswordChange ? '비밀번호가 변경 되었습니다' : '사용자 정보가 수정 되었습니다',
        });
        if (requirePasswordChange && input.new_password && user) {
          close?.();
          setRequired(user.identifier, false);
        }
        return refetch();
      }
      toast.show({
        kind: 'error',
        position: 'bottom-center',
        content: '사용자 정보가 수정 도중 문제가 발생 하였습니다',
      });
    },
    onError: (error) => {
      toast.show({
        kind: 'error',
        position: 'bottom-center',
        content: error instanceof GraphQLError ? error.message : '사용자 정보가 수정 도중 문제가 발생 하였습니다',
      });
    },
  });

  const handleSubmit = (values: UpdateUserFormValues) => {
    mutate({
      input: {
        name: requirePasswordChange ? undefined : values.name,
        phone_number: requirePasswordChange ? undefined : values.tel.replace(/\D/g, ''),
        mfa_method: !requirePasswordChange && values.mfa === 'SMS' ? 'SMS' : undefined,
        current_password: values.password,
        new_password: requirePasswordChange || values.is_change_password ? values.new_password : undefined,
        seller_account_id: user?.id ?? '',
      },
    });
  };

  return (
    <Modal
      closeButton={!requirePasswordChange}
      canClickOutside={!requirePasswordChange}
      canCloseEscapeKey={!requirePasswordChange}
      title={requirePasswordChange ? '비밀번호 변경' : '사용자 정보 수정'}
      opened={opened}
      onCancel={requirePasswordChange ? undefined : close}
      contentProps={{ scrollable: true, p: 0 }}
    >
      <UpdateUserFormProvider
        defaultValues={{
          is_password_change_required: requirePasswordChange,
          is_change_password: requirePasswordChange,
          email: user?.identifier,
          name: user?.name,
          tel: user?.phone_number,
          mfa: (user?.mfa_method ? user?.mfa_method : 'NONE') as UpdateUserFormValues['mfa'],
        }}
      >
        <Form
          requirePasswordChange={requirePasswordChange}
          loading={is_loading}
          onCancel={requirePasswordChange ? logout : close}
          onSubmit={handleSubmit}
        />
      </UpdateUserFormProvider>
    </Modal>
  );
};
